import { LocalDate } from "@js-joda/core";
import { getUserProfile } from "./backend/user-profiles";

type TaxCalculation = {
  gross: number;
  personalAllowance: number;
  taxable: number;

  incomeTax: number;
  nationalInsurance: number;
  studentLoan: number;
  pensionContribution: number;

  totalDeductions: number;
  net: number;
  effectiveRate: number;
};

type TaxBand = {
  upTo: number | null; // taxable income upper limit
  rate: number;
};

type IncomeTaxPolicy = {
  personalAllowance: number;
  paTaperStart: number;
  paTaperEnd: number;
  bands: TaxBand[];
};

type NiPolicy = {
  primaryThresholdWeekly: number;
  upperEarningsLimitWeekly: number;
  mainRate: number;
  additionalRate: number;
};

type StudentLoanPolicy = {
  planThresholds: Record<1 | 2 | 4 | 5, number>;
  postgraduateThreshold: number;
  planRate: number;
  postgraduateRate: number;
};

const TAX_POLICIES_RUK: Record<number, IncomeTaxPolicy> = {
  2025: {
    personalAllowance: 12_570,
    paTaperStart: 100_000,
    paTaperEnd: 125_140,
    bands: [
      { upTo: 37_700, rate: 0.2 },
      { upTo: 125_140, rate: 0.4 },
      { upTo: null, rate: 0.45 },
    ],
  },
  2026: {
    personalAllowance: 12_570,
    paTaperStart: 100_000,
    paTaperEnd: 125_140,
    bands: [
      { upTo: 37_700, rate: 0.2 },
      { upTo: 125_140, rate: 0.4 },
      { upTo: null, rate: 0.45 },
    ],
  },
};

const TAX_POLICIES_SCOTLAND: Record<number, IncomeTaxPolicy> = {
  2025: {
    personalAllowance: 12_570,
    paTaperStart: 100_000,
    paTaperEnd: 125_140,
    bands: [
      { upTo: 2_827, rate: 0.19 },
      { upTo: 14_921, rate: 0.2 },
      { upTo: 31_092, rate: 0.21 },
      { upTo: 62_430, rate: 0.42 },
      { upTo: 125_140, rate: 0.45 },
      { upTo: null, rate: 0.48 },
    ],
  },
};

const NI_POLICIES: Record<number, NiPolicy> = {
  2025: {
    primaryThresholdWeekly: 242,
    upperEarningsLimitWeekly: 967,
    mainRate: 0.08,
    additionalRate: 0.02,
  },
  2026: {
    primaryThresholdWeekly: 242,
    upperEarningsLimitWeekly: 967,
    mainRate: 0.08,
    additionalRate: 0.02,
  },
};

const STUDENT_LOAN_POLICIES: Record<number, StudentLoanPolicy> = {
  2025: {
    planThresholds: {
      1: 26_065,
      2: 28_470,
      4: 32_745,
      5: 25_000,
    },
    postgraduateThreshold: 21_000,
    planRate: 0.09,
    postgraduateRate: 0.06,
  },
};

type TaxCodeMode = "normal" | "basic" | "higher" | "additional" | "none";

type ParsedTaxCode = {
  allowance: number;
  mode: TaxCodeMode;
};

function getTaxYearKey(taxYear: { start: LocalDate }): number {
  return taxYear.start.year();
}

function latestKey<T>(map: Record<number, T>): number {
  return Math.max(...Object.keys(map).map((x) => Number(x)));
}

function getIncomeTaxPolicy(yearKey: number, country: "rUK" | "scotland") {
  const map = country === "scotland" ? TAX_POLICIES_SCOTLAND : TAX_POLICIES_RUK;
  return map[yearKey] ?? map[latestKey(map)];
}

function getNiPolicy(yearKey: number) {
  return NI_POLICIES[yearKey] ?? NI_POLICIES[latestKey(NI_POLICIES)];
}

function getStudentLoanPolicy(yearKey: number) {
  return (
    STUDENT_LOAN_POLICIES[yearKey] ??
    STUDENT_LOAN_POLICIES[latestKey(STUDENT_LOAN_POLICIES)]
  );
}

function parseTaxCode(code?: string | null): ParsedTaxCode {
  const fallback: ParsedTaxCode = { allowance: 12_570, mode: "normal" };
  if (!code) return fallback;

  const trimmed = code.trim().toUpperCase();

  if (trimmed === "NT") return { allowance: 0, mode: "none" };
  if (trimmed === "BR") return { allowance: 0, mode: "basic" };
  if (trimmed === "D0") return { allowance: 0, mode: "higher" };
  if (trimmed === "D1") return { allowance: 0, mode: "additional" };
  if (trimmed === "0T") return { allowance: 0, mode: "normal" };

  const digits = trimmed.match(/\d+/g)?.join("");
  if (digits) {
    return { allowance: parseInt(digits, 10) * 10, mode: "normal" };
  }

  return fallback;
}

function applyPersonalAllowanceTaper(
  allowance: number,
  adjustedNetIncome: number,
  policy: IncomeTaxPolicy,
): number {
  if (adjustedNetIncome <= policy.paTaperStart) return allowance;
  if (adjustedNetIncome >= policy.paTaperEnd) return 0;

  const reduction = (adjustedNetIncome - policy.paTaperStart) / 2;
  return Math.max(0, allowance - reduction);
}

export function calculateIncomeTax(
  taxableIncome: number,
  policy: IncomeTaxPolicy,
  mode: TaxCodeMode,
  basicBandExtension: number,
): number {
  if (taxableIncome <= 0) return 0;

  if (mode === "none") return 0;
  if (mode === "basic") return taxableIncome * 0.2;
  if (mode === "higher") return taxableIncome * 0.4;
  if (mode === "additional") return taxableIncome * 0.45;

  let remaining = taxableIncome;
  let incomeTax = 0;

  const bands = [...policy.bands];
  if (basicBandExtension > 0 && bands.length > 0 && bands[0].upTo) {
    bands[0] = { ...bands[0], upTo: bands[0].upTo + basicBandExtension };
  }

  let previousLimit = 0;
  for (const band of bands) {
    const limit = band.upTo ?? Infinity;
    const bandSize = limit - previousLimit;
    const amount = Math.min(remaining, bandSize);
    if (amount <= 0) break;

    incomeTax += amount * band.rate;
    remaining -= amount;
    previousLimit = limit;
  }

  if (remaining > 0) {
    incomeTax += remaining * bands[bands.length - 1].rate;
  }

  return incomeTax;
}

export function calculateEmployeeNI(gross: number, yearKey: number): number {
  const policy = getNiPolicy(yearKey);
  const {
    primaryThresholdWeekly,
    upperEarningsLimitWeekly,
    mainRate,
    additionalRate,
  } = policy;
  const primaryThreshold = primaryThresholdWeekly * 52;
  const upperEarningsLimit = upperEarningsLimitWeekly * 52;

  let ni = 0;

  if (gross > primaryThreshold) {
    const mainBand = Math.min(gross, upperEarningsLimit) - primaryThreshold;
    ni += mainBand * mainRate;
  }

  if (gross > upperEarningsLimit) {
    ni += (gross - upperEarningsLimit) * additionalRate;
  }

  return round2(ni);
}

export function calculateStudentLoan(
  gross: number,
  yearKey: number,
  plan?: 1 | 2 | 4 | 5,
  postgraduate?: boolean,
): number {
  if (!plan && !postgraduate) return 0;

  const policy = getStudentLoanPolicy(yearKey);
  let repayment = 0;

  if (plan) {
    const threshold = policy.planThresholds[plan];
    repayment += Math.max(0, gross - threshold) * policy.planRate;
  }

  if (postgraduate) {
    repayment +=
      Math.max(0, gross - policy.postgraduateThreshold) *
      policy.postgraduateRate;
  }

  return round2(repayment);
}

export function calculateIndividualUkTaxAndNI({
  grossPounds,
  userId,
  taxYear,
}: {
  grossPounds: number;
  userId: string;
  taxYear: { start: LocalDate; end: LocalDate };
}): TaxCalculation {
  const user = getUserProfile(userId);
  const yearKey = getTaxYearKey(taxYear);

  const pensionRate = user.pension?.contributionRate ?? 0;
  const pensionScheme = user.pension?.scheme ?? "net-pay";
  const pensionContribution = grossPounds * pensionRate;

  const grossForNI =
    pensionScheme === "salary-sacrifice"
      ? grossPounds - pensionContribution
      : grossPounds;

  const grossForTaxable =
    pensionScheme === "net-pay" || pensionScheme === "salary-sacrifice"
      ? grossPounds - pensionContribution
      : grossPounds;

  const adjustedNetIncome =
    pensionScheme === "relief-at-source"
      ? grossPounds - pensionContribution / 0.8
      : grossForTaxable;

  const taxPolicy = getIncomeTaxPolicy(yearKey, user.country);
  const parsedTaxCode = parseTaxCode(user.taxCode);

  let personalAllowance =
    parsedTaxCode.allowance || taxPolicy.personalAllowance;

  if (parsedTaxCode.mode === "normal") {
    personalAllowance = applyPersonalAllowanceTaper(
      personalAllowance,
      adjustedNetIncome,
      taxPolicy,
    );
  } else {
    personalAllowance = 0;
  }

  const taxable = Math.max(0, grossForTaxable - personalAllowance);

  const basicBandExtension =
    user.country === "rUK" && pensionScheme === "relief-at-source"
      ? pensionContribution / 0.8
      : 0;

  const incomeTax = calculateIncomeTax(
    taxable,
    taxPolicy,
    parsedTaxCode.mode,
    basicBandExtension,
  );

  const nationalInsurance = calculateEmployeeNI(grossForNI, yearKey);

  const studentLoan = calculateStudentLoan(
    grossForNI,
    yearKey,
    user.studentLoan?.plan ?? undefined,
    user.studentLoan?.postgraduate ?? undefined,
  );

  const totalDeductions =
    incomeTax + nationalInsurance + studentLoan + pensionContribution;
  const net = grossPounds - totalDeductions;
  const effectiveRate = grossPounds === 0 ? 0 : totalDeductions / grossPounds;

  return {
    gross: round2(grossPounds),
    personalAllowance: round2(personalAllowance),
    taxable: round2(taxable),

    incomeTax: round2(incomeTax),
    nationalInsurance,
    studentLoan,
    pensionContribution: round2(pensionContribution),

    totalDeductions: round2(totalDeductions),
    net: round2(net),
    effectiveRate: round4(effectiveRate),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}
